import { useState, useEffect, useRef } from 'react';

/**
 * Hook for countdown timer based on timer_ends_at timestamp
 * @param timerEndsAt - ISO 8601 timestamp string (e.g., "2026-01-08T12:11:42.111Z")
 * @returns Remaining seconds (0 when timer expires)
 */
export const useCountdownTimer = (timerEndsAt: string | null): number => {
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!timerEndsAt) {
      setRemainingSeconds(0);
      return;
    }

    const calculateRemainingSeconds = (): number => {
      const now = new Date().getTime();
      const endsAt = new Date(timerEndsAt).getTime();
      const diff = Math.floor((endsAt - now) / 1000);
      return Math.max(0, diff);
    };

    // Calculate initial remaining seconds
    const initialSeconds = calculateRemainingSeconds();
    setRemainingSeconds(initialSeconds);

    // If timer already expired, don't start interval
    if (initialSeconds <= 0) {
      return;
    }

    // Update every second
    intervalRef.current = setInterval(() => {
      const seconds = calculateRemainingSeconds();
      setRemainingSeconds(seconds);

      // Clear interval when timer expires
      if (seconds <= 0 && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, 1000);

    // Cleanup on unmount or when timerEndsAt changes
    // eslint-disable-next-line consistent-return
    return () => {
      const currentInterval = intervalRef.current;
      if (currentInterval) {
        clearInterval(currentInterval);
      }
      intervalRef.current = null;
    };
  }, [timerEndsAt]);

  return remainingSeconds;
};
